import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  preferencesService,
  type PreferenceEntityType,
  type UserEntityPreference
} from '../../services/preferences';
import type { RootState } from '../store';

type PreferenceMap = Record<PreferenceEntityType, Record<string, UserEntityPreference>>;

type PreferencesState = {
  byType: PreferenceMap;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  lastLoadedAt: string | null;
};

const PREFERENCES_CACHE_TTL_MS = 1000 * 60 * 60; // 60 minutes
const PREFERENCES_FAILED_RETRY_MS = 1000 * 60 * 5; // 5 minutes

const emptyMap = (): PreferenceMap => ({
  composer: {},
  work: {},
  artist: {},
  recording: {}
});

const initialState: PreferencesState = {
  byType: emptyMap(),
  status: 'idle',
  error: null,
  lastLoadedAt: null
};

const getStaleForAge = (lastLoadedAt: string | null, maxAgeMs: number): boolean => {
  if (!lastLoadedAt) return true;
  const parsed = Date.parse(lastLoadedAt);
  if (!Number.isFinite(parsed)) return true;
  return Date.now() - parsed > maxAgeMs;
};

const shouldRefetchPreferences = (slice: PreferencesState): boolean => {
  if (slice.status === 'loading') return false;
  if (slice.status === 'idle') return true;
  const maxAge = slice.status === 'failed' ? PREFERENCES_FAILED_RETRY_MS : PREFERENCES_CACHE_TTL_MS;
  return getStaleForAge(slice.lastLoadedAt, maxAge);
};

export const fetchPreferences = createAsyncThunk<
  UserEntityPreference[],
  { force?: boolean } | void,
  { state: RootState }
>('preferences/fetchAll', async () => {
  return preferencesService.list();
}, {
  condition: (arg, { getState }) => {
    const { preferences } = getState();
    const force = Boolean((arg as { force?: boolean } | undefined)?.force);
    if (force) return true;
    return shouldRefetchPreferences(preferences);
  }
});

export const upsertPreference = createAsyncThunk<
  UserEntityPreference,
  { entityType: PreferenceEntityType; entityId: string; weight: number },
  { state: RootState }
>('preferences/upsert', async (input) => {
  const saved = await preferencesService.upsert(input);
  if (!saved) {
    throw new Error('Preference could not be saved.');
  }
  return saved;
});

export const removePreference = createAsyncThunk<
  { entityType: PreferenceEntityType; entityId: string },
  { entityType: PreferenceEntityType; entityId: string },
  { state: RootState }
>('preferences/remove', async (input) => {
  try {
    await preferencesService.remove(input.entityType, input.entityId);
  } catch (error: any) {
    const status = error?.response?.status ?? error?.status;
    if (status !== 404) {
      throw error;
    }
  }
  return input;
});

const mutateStateWithList = (state: PreferencesState, payload: UserEntityPreference[]) => {
  const next = emptyMap();
  for (const entry of payload) {
    if (!entry || !entry.entityId) continue;
    next[entry.entityType][entry.entityId] = entry;
  }
  state.byType = next;
  state.lastLoadedAt = new Date().toISOString();
};

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreferences.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        mutateStateWithList(state, action.payload);
      })
      .addCase(fetchPreferences.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? 'Failed to load preferences.';
        state.lastLoadedAt = new Date().toISOString();
      })
      .addCase(upsertPreference.fulfilled, (state, action) => {
        const entry = action.payload;
        state.byType[entry.entityType][entry.entityId] = entry;
        state.error = null;
        state.lastLoadedAt = new Date().toISOString();
      })
      .addCase(upsertPreference.rejected, (state, action) => {
        state.error = action.error?.message ?? 'Failed to save preference.';
      })
      .addCase(removePreference.fulfilled, (state, action) => {
        const { entityType, entityId } = action.payload;
        delete state.byType[entityType][entityId];
        state.error = null;
        state.lastLoadedAt = new Date().toISOString();
      })
      .addCase(removePreference.rejected, (state, action) => {
        state.error = action.error?.message ?? 'Failed to remove preference.';
      });
  }
});

export const preferencesReducer = preferencesSlice.reducer;

const selectPreferencesState = (state: RootState) => state.preferences;

export const selectPreferencesStatus = (state: RootState) =>
  selectPreferencesState(state).status;

export const selectPreferencesLastLoadedAt = (state: RootState) =>
  selectPreferencesState(state).lastLoadedAt;

export const selectPreference = (
  state: RootState,
  entityType: PreferenceEntityType,
  entityId: string
): UserEntityPreference | undefined => selectPreferencesState(state).byType[entityType]?.[entityId];

export const selectPreferencesShouldRefresh = (state: RootState): boolean => {
  const slice = selectPreferencesState(state);
  return shouldRefetchPreferences(slice);
};

export const preferencesSelectors = {
  selectPreferencesStatus,
  selectPreferencesLastLoadedAt,
  selectPreference,
  selectPreferencesShouldRefresh
};
